










































































































import Vue, { PropType } from 'vue';
import { OrgUser } from '@/models/orgStructure';
import { MemberRole, WebRequestStatus } from '@/models';
import store from '@/store';
import * as Utils from '@/utils';

import LazyRenderingList from '@/components/LazyRenderingList.vue';
import OrganizationRoleInfo from '@/components/info/OrganizationRoleInfo.vue';
import OrganizationUserSlug from '@/components/OrganizationUserSlug.vue';
import OrganizationUserCreate from '@/components/OrganizationUserCreate.vue';
import OrganizationUserCreateMultiple from '@/components/OrganizationUserCreateMultiple.vue';
import OrganizationUserMembership from '@/components/OrganizationUserMembership.vue';
import OrgRoles from '@/components/roles/OrgRoles.vue';
import PersistableItem from '@/components/PersistableItem.vue';
import RoleSwitch from '@/components/RoleSwitch.vue';

type MembershipMap = {
  [key in MemberRole]: number;
};

interface OrgUserWithRolesMap extends OrgUser {
  roles: MembershipMap;
  isActive: boolean;
}

type UserStatusList = [OrgUserWithRolesMap, WebRequestStatus][];

export default Vue.extend({
  components: {
    RoleSwitch,
    LazyRenderingList,
    OrganizationRoleInfo,
    OrganizationUserSlug,
    OrganizationUserCreate,
    OrganizationUserCreateMultiple,
    OrganizationUserMembership,
    OrgRoles,
    PersistableItem
  },
  props: {
    filterInput: {
      type: String,
      required: true
    },
    isFilterFocused: Boolean,
    users: Array as PropType<[OrgUser, WebRequestStatus][]>,
    createEnabled: Boolean
  },
  computed: {
    filter(): string {
      return Utils.trimBrackets(this.filterInput.trim());
    },

    all(): OrgUserWithRolesMap[] {
      return this.withRoles.map(([u]) => u);
    },

    withRoles(): UserStatusList {
      const withRolesAndIsActive: [
        OrgUserWithRolesMap,
        WebRequestStatus
      ][] = this.users.map(([u, status]) => {
        const rolesMap = u.memberships.reduce(
          (ships: MembershipMap, membership) => {
            ships[membership.role] = (ships[membership.role] ?? 0) + 1;
            return ships;
          },
          {} as MembershipMap
        );

        const isActive = u.memberships.some(m => m.isActive);

        return [
          {
            ...u,
            roles: rolesMap,
            isActive
          },
          status
        ];
      });

      const [active, archived] = withRolesAndIsActive.reduce(
        (acc, elem) => {
          const [user] = elem;
          const [active, archived] = acc;
          if (user.isActive) {
            return [[...active, elem], archived];
          } else {
            return [active, [...archived, elem]];
          }
        },
        [[], []] as [UserStatusList, UserStatusList]
      );

      return [...active, ...archived];
    },

    filtered(): [OrgUserWithRolesMap, WebRequestStatus][] {
      if (!this.filter) {
        return this.withRoles;
      }

      return Utils.findTerm(this.filter, this.withRoles, ([user]) => [
        user.name,
        user.emails.map(ue => ue.email).join(' ')
      ]);
    },

    showUserCreate(): boolean {
      return (
        this.createEnabled && this.noFilterMatch && !this.showMultiUserCreate
      );
    },

    noFilterMatch(): boolean {
      return this.users.length > 0 && this.filtered.length == 0;
    },

    showMultiUserCreate(): boolean {
      const tokenCount = Utils.parseByDelimiter(this.filterInput.trim()).length;

      return tokenCount > 1;
    }
  },

  methods: {
    // This narrows the type from OrganizationUserCreateMultiple, but we know we can
    // only get emails here.
    createUser(email: string) {
      this.$emit('create-user', email);
    },

    createUsers(emails: string[]) {
      this.$emit('create-users', emails);
    },

    userClick({ userId }: OrgUserWithRolesMap) {
      const orgId = this.$route.params['organizationId'] as string;
      this.$router.push({
        name: 'OrganizationUser',
        params: { organizationId: orgId, userId: `${userId}` }
      });
    },

    updateFilter(value: string) {
      this.$emit('update-filter', value);
    },

    // OrganizationUserCreateMultiple emits (OrgUser | string)[], but
    // since we know we aren't passing in any allowedUsers (only blockingUsers),
    // we know the emitted unions must be all emails (string[]).
    addAllUsers(emails: string[]) {
      this.$emit('create-users', emails);
    },

    clearFilter() {
      this.$emit('update-filter', '');
    },

    changeOrgRole(orgUser: OrgUser) {
      store.dispatch.org.updateUserRoles(orgUser);
    }
  },

  watch: {
    filtered(current, prev) {
      if (current.length != prev.length) {
        this.$emit('filter', { count: current.length });
      }
    }
  }
});
